import { useState } from 'react';
import styled from '@emotion/styled';
import { useSetAtom } from 'jotai';
import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
import { DTOProfileCardType } from 'src/apis/dto/DTOProfileCard';
import Gap from 'src/components/Gap';
import ModalLayout, { ModalLayoutProps, MobileModalType, ModalHeader, ModalHeaderTitle, ModalLoading } from 'src/components/ModalLayout';
import { DESC_TRANSLATION_KEYS, TITLE_TRANSLATION_KEYS } from 'src/components/Report/types';
import usePreloadByEntryPoint from 'src/hooks/usePreloadByEntryPoint';
import { showModalAtom } from 'src/stores/modal/atoms';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { ModalType } from 'src/types/Modal';
import { ReportType, ReportEventParam } from 'src/types/report';
import { pushEvent } from 'src/utils/event-gateway';
import { hoverAndActiveCss } from 'src/utils/styled';
const DSAReportModal = dynamic(() => import('src/components/Report/DSAReportModal'));
const Container = styled.div`
  padding: 0px;
  padding-bottom: 16px;
`;
const Reason = styled.button`
  display: flex;
  align-items: center;
  width: 100%;

  background: transparent;
  border-radius: 4px;
  padding: 12px 24px;

  text-align: ${({
  theme
}) => theme.isRtl ? 'right' : 'left'};
  font-style: normal;
  font-weight: 400;
  font-size: 15px;
  line-height: 18px;
  color: ${({
  theme
}) => theme.color.content12};
  cursor: pointer;

  ${({
  theme
}) => theme.screenSize.tablet} {
    font-size: 16px;
    line-height: 19px;
  }

  ${({
  theme
}) => hoverAndActiveCss(`
      background-color: ${theme.color.surface4};
      border-radius: 0px;
  `)}
`;
const ReasonBox = styled.div`
  flex: 1;
  min-height: 38px;
`;
const ReasonDesc = styled.p`
  color: ${({
  theme
}) => theme.color.content8};
  text-align: ${({
  theme
}) => theme.isRtl ? 'right' : 'left'};
  font-size: 12px;
`;
const MoreIconBox = styled.div`
  display: flex;
  align-items: center;
`;
const MoreIcon = styled.img`
  height: 20px;
  width: 20px;
  ${({
  theme
}) => theme.isRtl && `
    transform: scaleX(-1);
  `}
`;
export interface Props extends ModalLayoutProps {
  cardId: string;
  cardType: Exclude<DTOProfileCardType, 'MATCH'>;
  reportTypes: ReportType[];
  visibleProfileImageOption: boolean;
  onReport?: (reportType: ReportType) => void | Promise<void>;
  eventParams?: ReportEventParam;
}
export default function ProfileReportModal({
  reportTypes,
  cardId,
  cardType,
  onReport,
  visibleProfileImageOption,
  eventParams,
  onClose
}: Props) {
  const showModal = useSetAtom(showModalAtom);
  const {
    t
  } = useTranslation();
  const reasons = reportTypes.map(type => ({
    labelKey: TITLE_TRANSLATION_KEYS[type],
    descKey: DESC_TRANSLATION_KEYS[type],
    type
  }));
  const [isLoading, setIsLoading] = useState(false);
  const dsaReportButtonRef = usePreloadByEntryPoint(() => {
    import('src/components/Report/DSAReportModal');
  });
  const handleClick = async (reason: (typeof reasons)[number]) => {
    const targetEventParam = {
      ...eventParams,
      target: `report.${reason.type}`
    };
    pushEvent({
      eventName: EVENT_NAME.REPORT_BLOCK_REASON_POPUP_SELECT,
      eventType: EVENT_TYPE.REPORT,
      eventParams: {
        ...targetEventParam,
        action_category: 'click'
      }
    });
    if (reason.type === 'DSA_ILLEGAL_CONTENT') {
      showModal({
        key: ModalType.DSA_REPORT,
        component: () => <DSAReportModal cardId={cardId} cardType={cardType} visibleProfileImageOption={visibleProfileImageOption} eventParams={targetEventParam} />
      });
      return;
    }
    setIsLoading(true);
    await onReport?.(reason.type);
    setIsLoading(false);
  };
  return <ModalLayout mobileType={MobileModalType.BOTTOM_SHEET} onClose={onClose} isDim data-testid='ProfileReportModal' data-sentry-element="ModalLayout" data-sentry-component="ProfileReportModal" data-sentry-source-file="index.tsx">
      {isLoading && <ModalLoading type='absolute' data-testid='Report-Modal-loading' />}
      <ModalHeader data-sentry-element="ModalHeader" data-sentry-source-file="index.tsx">
        <ModalHeaderTitle mobileType={MobileModalType.BOTTOM_SHEET} data-sentry-element="ModalHeaderTitle" data-sentry-source-file="index.tsx">
          {t('REPORT_TITLE')}
        </ModalHeaderTitle>
      </ModalHeader>
      <Container data-sentry-element="Container" data-sentry-source-file="index.tsx">
        {reasons.map(reason => <Reason type='button' onClick={() => handleClick(reason)} key={`Report-Modal-reason-${reason.type}`} data-testid='Report-Modal-reason' data-report-type={reason.type} ref={reason.type === 'DSA_ILLEGAL_CONTENT' ? dsaReportButtonRef : undefined}>
            <ReasonBox>
              {t(reason.labelKey)}
              {reason.descKey != null && <ReasonDesc>{t(reason.descKey)}</ReasonDesc>}
            </ReasonBox>
            {reason.type === 'DSA_ILLEGAL_CONTENT' && <MoreIconBox>
                <Gap width={16} />
                <MoreIcon src='/images/icons/icChevronRightRegular.svg' alt='icMore' />
              </MoreIconBox>}
          </Reason>)}
      </Container>
    </ModalLayout>;
}